export const LEARN_MODULES = [
  {
    slug: "whisky-101",
    category: "Spirits · 101",
    title: "Whisky 101: Every kind, made simple.",
    read: "9 min",
    excerpt: "Scotch, Bourbon, Rye, Irish, Japanese, Indian single malt — what actually makes them different?",
    image: "https://images.pexels.com/photos/2198400/pexels-photo-2198400.jpeg?auto=compress&cs=tinysrgb&w=1200",
    body: [
      { heading: "What is whisky?", paragraphs: [
        "Whisky (or whiskey — the spelling depends on where it's made) is a spirit distilled from fermented grain and aged in wooden barrels. That's the entire idea. Everything else — smoke, sweetness, spice, colour — comes from four variables: the grain used, the water and yeast, how it's distilled, and how long (and in what) it's aged."
      ]},
      { heading: "The four ingredients", bullets: [
        { term: "Grain", def: "Barley (usually malted), corn, rye, wheat — each contributes different flavours." },
        { term: "Water", def: "The neutral canvas. Local water sources shape regional styles." },
        { term: "Yeast", def: "Converts sugars to alcohol; different yeast strains create different fruity/spicy notes." },
        { term: "Oak", def: "Where the magic happens. New charred oak vs. used sherry casks changes everything." },
      ]},
      { heading: "How whisky is made", paragraphs: [
        "The process from field to glass is remarkably universal — even if the labels look wildly different."
      ], bullets: [
        { term: "Grain selection", def: "Barley for malts, corn for bourbon, rye for rye whiskey. The choice defines the family." },
        { term: "Mashing", def: "Cooking crushed grain in hot water to extract fermentable sugars." },
        { term: "Fermentation", def: "Yeast turns those sugars into a rough, beer-like liquid called 'wash' at around 8% ABV." },
        { term: "Distillation", def: "Heating the wash in stills concentrates the alcohol. Scotch is usually pot-distilled twice; Irish is often triple-distilled." },
        { term: "Maturation", def: "The distillate rests in oak barrels for years. Wood, climate and time do the heavy lifting." },
        { term: "Cask selection", def: "Ex-bourbon barrels give vanilla; sherry casks add dried fruit; new charred oak brings caramel and spice." },
        { term: "Blending & bottling", def: "The master blender balances different casks and often reduces the strength with water before bottling." },
      ]},
      { heading: "The whisky families", bullets: [
        { term: "Scotch whisky", def: "Made in Scotland from malted barley. Aged at least 3 years. Often smoky (Islay) or floral (Speyside)." },
        { term: "Irish whiskey", def: "Triple-distilled and smoother. Made in Ireland, aged at least 3 years." },
        { term: "Bourbon", def: "American whiskey, at least 51% corn, aged in new charred oak. Sweet, caramel, vanilla." },
        { term: "Tennessee whiskey", def: "Legally bourbon plus the Lincoln County Process — charcoal-mellowed before ageing." },
        { term: "Rye whiskey", def: "Spicier than bourbon, at least 51% rye grain. The Manhattan and Sazerac base." },
        { term: "Canadian whisky", def: "Typically lighter and smoother, often called 'rye' colloquially in Canada." },
        { term: "Japanese whisky", def: "Style-wise similar to Scotch but often more delicate; increasingly world-class." },
        { term: "Indian whisky", def: "Amrut, Paul John, Rampur, Indri, Godawan — tropical climate ages spirits faster and adds intense character." },
      ]},
      { heading: "The six Scotch regions", bullets: [
        { term: "Highlands", def: "Vast region with everything from rich Dalmore to delicate Glenmorangie." },
        { term: "Lowlands", def: "Traditionally triple-distilled, light and grassy. Auchentoshan is the classic." },
        { term: "Speyside", def: "Home to Glenlivet, Glenfiddich, Macallan. Often fruity, sweet, sherry-influenced." },
        { term: "Islay", def: "Peated smoky monsters — Ardbeg, Lagavulin, Laphroaig." },
        { term: "Campbeltown", def: "Tiny region on the west coast, briny and complex. Springbank is the icon." },
        { term: "Islands", def: "Highland Park, Talisker, Jura — maritime with variable smoke." },
      ]},
      { heading: "Malt vs Grain vs Blends", bullets: [
        { term: "Single malt", def: "One distillery, 100% malted barley, pot-distilled." },
        { term: "Blended malt", def: "Malts from multiple distilleries blended together (no grain)." },
        { term: "Single grain", def: "One distillery, but uses other grains beyond barley." },
        { term: "Blended Scotch", def: "A mix of malt and grain whiskies from different distilleries — Johnnie Walker, Chivas, Ballantine's." },
        { term: "Grain whisky", def: "Made in continuous stills from grains like corn or wheat — often lighter." },
      ]},
      { heading: "Common flavour profiles", paragraphs: ["When bartenders and tasters describe whisky, they usually reach for one or more of these categories:"], bullets: [
        { term: "Fruity", def: "Apple, pear, dried apricot, orange peel." },
        { term: "Floral", def: "Heather, rose, honeysuckle." },
        { term: "Spicy", def: "Cinnamon, black pepper, clove, ginger." },
        { term: "Smoky / Peaty", def: "Bonfire, iodine, seaside — from burning peat during malting." },
        { term: "Woody", def: "Toasted oak, cedar, resin — from the barrel." },
        { term: "Vanilla", def: "The signature of ex-bourbon casks." },
        { term: "Caramel", def: "Toffee, butterscotch — often from long ageing in oak." },
        { term: "Nutty", def: "Almond, walnut, hazelnut." },
        { term: "Citrus", def: "Lemon, grapefruit, marmalade." },
      ]},
      { heading: "How to actually taste whisky", paragraphs: [
        "Pour a small measure (30ml) into a tulip-shaped glass. Look, swirl, and sniff first — most of what we call 'taste' is actually smell. Take a small sip, let it coat your tongue, and pay attention to what happens after you swallow (the 'finish').",
        "Then add a few drops of water — good whisky opens up. This isn't sacrilege; it's how master distillers taste."
      ]},
      { heading: "Neat, rocks, water, cask strength", bullets: [
        { term: "Neat", def: "Room temperature, no additions. The purist way." },
        { term: "On the rocks", def: "Over ice. Chills and slowly dilutes; great for stronger pours." },
        { term: "With water", def: "A few drops open up the aromas. Recommended for high-ABV whiskies." },
        { term: "Cask strength", def: "Bottled without dilution, usually 55-65% ABV. Add water to taste." },
      ]},
      { heading: "Whisky terminology, decoded", bullets: [
        { term: "ABV", def: "Alcohol By Volume. Standard whisky is 40%; cask strength can be 60%+." },
        { term: "Proof", def: "An older US measure. Proof = ABV × 2. 80-proof = 40% ABV." },
        { term: "NAS", def: "No Age Statement. The whisky's age isn't printed — could still be excellent." },
        { term: "Angel's share", def: "Whisky that evaporates from the cask during ageing — 1-2% per year in Scotland, more in India." },
        { term: "Finish", def: "The taste that lingers after swallowing. Also: additional ageing in a different cask (e.g., 'sherry finish')." },
        { term: "Mash bill", def: "The exact grain recipe. A bourbon's mash bill might be 70% corn, 20% rye, 10% malted barley." },
        { term: "Dram", def: "Informal Scottish term for a small pour of whisky." },
        { term: "Non-chill filtered", def: "The whisky isn't chilled and filtered before bottling — often preserves more flavour and texture." },
      ]},
      { heading: "Whisky in cocktails", paragraphs: [
        "Bourbon and rye are the workhorses of the classic cocktail canon. The Old Fashioned, Manhattan, Whisky Sour and Sazerac are all rooted in American whiskey. Blended Scotch works well with soda (a Highball), while single malts are usually best neat — a great cask deserves a good listen."
      ]},
      { heading: "Indian whisky, in one paragraph", paragraphs: [
        "For decades 'Indian whisky' meant molasses-based spirits sold as whisky within India. That's changed. Amrut, Paul John, Rampur, Indri and Godawan are now producing single malts that regularly win world awards. India's tropical climate ages spirits much faster — an Indian 4-year-old can taste like a 12-year Scotch. Try Paul John Nirvana for a friendly introduction, or Amrut Peated for smoke."
      ]},
      { heading: "Beginner recommendations", bullets: [
        { term: "If you like sweet coffee drinks", def: "Try a Bourbon (Woodford Reserve, Maker's Mark)." },
        { term: "If you like bitter / dark chocolate", def: "Try a peated Scotch (Talisker, Ardbeg 10)." },
        { term: "If you like light beers and citrus", def: "Try an Irish whiskey (Jameson, Bushmills) or Japanese Toki." },
        { term: "If you like sherry and dried fruit", def: "Try Speyside (Glenlivet 12, Macallan 12)." },
        { term: "If you want an Indian introduction", def: "Paul John Nirvana or Amrut Fusion." },
      ]},
      { heading: "One last thing", paragraphs: [
        "Drink responsibly — legal drinking age varies by Indian state and territory. If you're new to whisky, start small: 30ml with a splash of water, sip slowly, and give yourself time to notice what you actually like. Whisky is not a race."
      ]},
    ],
  },
  {
    slug: "wine-101",
    category: "Fermented · 101",
    title: "Wine 101: How to order without panicking.",
    read: "4 min",
    excerpt: "Red, white, rosé, sparkling — and the words that finally start to make sense.",
    image: "https://images.unsplash.com/photo-1456427651609-f494c36fbc45?auto=format&fit=crop&w=1200&q=80",
    body: [
      { paragraphs: ["Wine is fermented grape juice. Colour comes from whether the skins hang around during fermentation — reds soak, whites don't, rosés get a quick dip."] },
      { heading: "The five you should know", bullets: [
        { term: "Cabernet Sauvignon", def: "Bold red, dark fruit, tannic. Loves red meat." },
        { term: "Pinot Noir", def: "Light red, silky, food-friendly. The chameleon." },
        { term: "Chardonnay", def: "White that can be buttery (oaked) or crisp (unoaked). Read the label." },
        { term: "Sauvignon Blanc", def: "Zesty, herbal white. Salads, seafood, hot days." },
        { term: "Prosecco / Champagne", def: "Bubbles. One's Italian, one's French, both make everything better." },
      ]},
      { heading: "Words you'll hear", paragraphs: [
        "Tannin — that dry, gripping feeling on your gums (from grape skins and oak). Body — how heavy the wine feels in your mouth. Dry vs sweet — how much residual sugar is left after fermenting.",
        "'Legs' on the glass tell you about alcohol and viscosity, not quality."
      ]},
    ],
  },
  {
    slug: "beer-101",
    category: "Fermented · 101",
    title: "Beer 101: Lagers, ales & everything else.",
    read: "3 min",
    excerpt: "Two families, endless styles. Here's the map.",
    image: "https://images.unsplash.com/photo-1608270586620-248524c67de9?auto=format&fit=crop&w=1200&q=80",
    body: [
      { paragraphs: ["All beer is either a lager or an ale — the difference is the yeast and the temperature it ferments at. Lagers ferment cold and slow. Ales ferment warm and fast."] },
      { heading: "Lagers", paragraphs: ["Clean, crisp, refreshing. Think Kingfisher, Heineken, most Indian beers. Great with spicy food because they cool your mouth without fighting the flavours."] },
      { heading: "Ales", bullets: [
        { term: "IPA", def: "India Pale Ale. Bitter, hoppy, aromatic. Grapefruit, pine, tropical notes." },
        { term: "Wheat Beer", def: "Cloudy, soft, orange-y. Hoegaarden, Bira White. Brunch's best friend." },
        { term: "Stout", def: "Dark, roasty, coffee and chocolate notes. Guinness is the classic." },
      ]},
      { heading: "ABV & IBU", paragraphs: ["ABV is alcohol strength (usually 4-8%). IBU is bitterness — a Kingfisher is around 10, a serious IPA can hit 70."] },
    ],
  },
  {
    slug: "cocktail-basics",
    category: "Mixology · 101",
    title: "Cocktail Basics: The five techniques that unlock everything.",
    read: "5 min",
    excerpt: "Shake, stir, build, muddle, layer — every drink is one of these.",
    image: "https://images.pexels.com/photos/33337325/pexels-photo-33337325.jpeg?auto=compress&cs=tinysrgb&w=1200",
    body: [
      { paragraphs: ["A cocktail is just a considered mixture — usually a spirit, something sweet, something sour or bitter, and something to dilute or lengthen it. The techniques dictate the mouthfeel."] },
      { heading: "The five moves", bullets: [
        { term: "Shake", def: "For anything with juice, dairy or egg. Aerates, chills fast, adds tiny bubbles. Shake HARD for 10-12 seconds." },
        { term: "Stir", def: "For all-spirit drinks like Martinis and Manhattans. Chills without diluting or clouding." },
        { term: "Build", def: "Layer directly in the glass. Gin & Tonic, Mojito, Old Fashioned. Simple, elegant." },
        { term: "Muddle", def: "Gently press herbs or fruit to release oils. Never shred — bruised mint tastes bitter." },
        { term: "Layer", def: "Slowly pour heavier liquids first, then float lighter ones on the back of a spoon." },
      ]},
      { heading: "Your first three tools", paragraphs: ["A shaker (Boston or three-piece), a jigger (measure!), and a strainer. That's it. Everything else is nice-to-have."] },
    ],
  },
  {
    slug: "liquor-vs-liqueur",
    category: "Glossary",
    title: "Liquor vs Liqueur: One letter, huge difference.",
    read: "2 min",
    excerpt: "One's a base. The other's a modifier. Get it right at the bar.",
    image: "https://images.unsplash.com/photo-1582819509237-d5b75f20ff7a?auto=format&fit=crop&w=1200&q=80",
    body: [
      { heading: "Liquor", paragraphs: ["Distilled alcohol. High ABV, no added sugar. The 'base spirits' — Whisky, Vodka, Rum, Gin, Tequila, Brandy. Usually 35-50% ABV."] },
      { heading: "Liqueur", paragraphs: ["A liquor that's been sweetened and flavoured with fruits, spices, herbs, or cream. Lower ABV (15-30%), sweet, used to add character to cocktails. Examples: Cointreau (orange), Baileys (cream), Kahlua (coffee), Campari (bitter herbs)."] },
      { heading: "The rule of thumb", paragraphs: ["If it's sweet on its own, it's probably a liqueur. If it burns clean and bright, it's liquor."] },
    ],
  },
  {
    slug: "understanding-abv",
    category: "Glossary",
    title: "Understanding ABV: What that percentage actually means.",
    read: "3 min",
    excerpt: "ABV, proof, standard drinks — decoded.",
    image: "https://images.unsplash.com/photo-1514362545857-3bc16c4c7d1b?auto=format&fit=crop&w=1200&q=80",
    body: [
      { paragraphs: ["ABV = Alcohol By Volume. If a bottle says 40% ABV, 40% of the liquid inside is pure ethanol. That's it."] },
      { heading: "Rough map", bullets: [
        { term: "Beer", def: "4-8% ABV" },
        { term: "Wine", def: "11-15% ABV" },
        { term: "Spirits", def: "35-50% ABV" },
        { term: "Cocktails", def: "10-30% ABV, depending on the recipe" },
      ]},
      { heading: "Proof", paragraphs: ["An older American measure. Proof = ABV × 2. So an 80-proof whisky is 40% ABV."] },
      { heading: "One standard drink", paragraphs: ["In India, roughly 30ml of spirit at 40% ABV, or a 330ml beer at 5%, or a 150ml glass of wine at 12%. Know how many standard drinks are actually in what you're pouring."] },
    ],
  },
  {
    slug: "understanding-glassware",
    category: "Craft",
    title: "Understanding Glassware: Why the shape matters.",
    read: "4 min",
    excerpt: "Not snobbery — physics. Here's how each glass changes your drink.",
    image: "https://images.unsplash.com/photo-1470337458703-46ad1756a187?auto=format&fit=crop&w=1200&q=80",
    body: [
      { paragraphs: ["The right glass changes temperature, aroma delivery, and how a drink hits your palate. It's not fussy — it's function."] },
      { heading: "The essentials", bullets: [
        { term: "Rocks / Old Fashioned", def: "Short, wide, thick base. For spirits neat or over one big ice cube." },
        { term: "Highball / Collins", def: "Tall and slim. Long drinks with a lot of mixer — Mojito, G&T, Moscow Mule." },
        { term: "Coupe", def: "The vintage saucer shape. Shaken cocktails without ice — Daiquiri, Cosmo, Espresso Martini." },
        { term: "Martini", def: "The V-shape. Same use as coupe, more theatrical, slightly harder to sip." },
        { term: "Champagne Flute", def: "Tall and narrow. Traps bubbles longer. For sparkling wines and French 75." },
        { term: "Wine Glass", def: "Big bowl = more air = fuller aroma. Reds want big bowls, whites want smaller." },
        { term: "Copper Mug", def: "For Moscow Mules. Keeps things ice-cold and adds a metallic tang." },
      ]},
      { heading: "The one hack", paragraphs: ["Chill your glasses before serving cold drinks. Ten minutes in the freezer transforms the experience."] },
    ],
  },
  {
    slug: "rum-101",
    category: "Spirits · 101",
    title: "Rum 101: The sugarcane spirit, decoded.",
    read: "6 min",
    excerpt: "White, gold, dark, spiced, agricole, overproof — from Old Monk to Havana Club.",
    image: "https://images.unsplash.com/photo-1541546006121-5c3bc5e8c7b9?auto=format&fit=crop&w=1200&q=80",
    body: [
      { heading: "What rum is", paragraphs: [
        "Rum is a spirit made from sugarcane — either from fresh sugarcane juice, or (more commonly) from molasses, a syrupy by-product of sugar production. Almost every country in the tropics that grew sugar eventually made rum: the Caribbean, Latin America, India, and the Indian Ocean islands all have deep rum traditions."
      ]},
      { heading: "Sugarcane juice vs molasses", bullets: [
        { term: "Molasses rum", def: "Most rum in the world. Sweet, rich, chocolatey — Bacardi, Havana Club, Old Monk, Captain Morgan." },
        { term: "Sugarcane juice rum (Agricole)", def: "Made from fresh-pressed cane juice. Grassy, funky, vegetal — the classic Martinique style." },
      ]},
      { heading: "How rum is made", bullets: [
        { term: "Fermentation", def: "Yeast converts the sugars in molasses or cane juice into alcohol." },
        { term: "Distillation", def: "Copper pot stills give heavier, funkier rum (Jamaica). Column stills give lighter, cleaner rum (Puerto Rico)." },
        { term: "Maturation", def: "Rum is often aged in ex-bourbon barrels — tropical climate ages it fast." },
        { term: "Blending", def: "Master blenders mix different ages and stills to build a consistent style." },
      ]},
      { heading: "The rum styles", bullets: [
        { term: "White rum", def: "Unaged or lightly aged and filtered. Clean and versatile — the Mojito and Daiquiri base." },
        { term: "Gold rum", def: "Lightly aged in oak — a warmer, sweeter Bacardi Gold or Mount Gay Eclipse." },
        { term: "Dark rum", def: "Heavier, molasses-forward, often with added caramel colour. Old Monk is the Indian icon." },
        { term: "Aged rum", def: "Rested in oak for 8, 12, 23+ years. Sipping rum — think Diplomático or Zacapa." },
        { term: "Spiced rum", def: "Infused with spices like cinnamon and vanilla — Captain Morgan, Kraken." },
        { term: "Agricole rum", def: "Made from fresh sugarcane juice — grassier and funkier. Martinique specialty." },
        { term: "Overproof rum", def: "Bottled at 60%+ ABV — Wray & Nephew is the Jamaican classic. Use sparingly in cocktails." },
      ]},
      { heading: "Regional rum styles", bullets: [
        { term: "Cuba", def: "Light, dry, elegant — Havana Club." },
        { term: "Puerto Rico", def: "Similar to Cuba: clean, column-still lightness. Bacardi's spiritual home." },
        { term: "Jamaica", def: "Funky, pot-distilled, high ester — Appleton, Wray & Nephew." },
        { term: "Martinique / Guadeloupe", def: "Agricole rums with grassy vegetal character." },
        { term: "Barbados", def: "Balanced, rounded, elegant — Mount Gay is the oldest rum brand in the world." },
        { term: "Guyana / Demerara", def: "Rich, heavy, molasses-forward — El Dorado." },
        { term: "India", def: "Home of Old Monk, McDowell's, and modern craft distilleries like Camikara and Amrut." },
      ]},
      { heading: "How to taste rum", paragraphs: [
        "Pour a small measure into a tulip glass. Nose gently — rum's aromas can be intense. Sip slowly. Aged rums reward the same attention as whisky — swirl, sniff, sip, wait for the finish. White rums are for mixing; aged rums are for sipping."
      ]},
      { heading: "Rum in cocktails", paragraphs: [
        "Rum is arguably the world's most versatile cocktail base. The Mojito, Daiquiri, Piña Colada, Mai Tai, Dark & Stormy and Cuba Libre all live here. Use white rum for citrusy drinks, gold for classic tiki, dark for stirred cocktails, and spiced for anything long and refreshing."
      ]},
      { heading: "Beginner recommendations", bullets: [
        { term: "Old Monk 7 Year", def: "Sweet, vanilla-forward, iconic — the friendliest introduction in India." },
        { term: "Bacardi Superior", def: "Clean white rum for Mojitos and Daiquiris." },
        { term: "Havana Club 7", def: "A step up into sipping rum — warm and elegant." },
        { term: "Captain Morgan Spiced", def: "Sweet, easy, cola-friendly." },
      ]},
      { heading: "Responsible drinking", paragraphs: [
        "Legal drinking age varies by Indian state and territory. Rum drinks are often stronger than they taste — pace yourself, drink water, and know your limits."
      ]},
    ],
  },
];
